import {
  PERSISTED_VARIANT_OPTIONS_KEY,
  STORE_NAME,
} from '@/contexts/LocalStore';
import {
  applyOptionValuesToVariant,
  applyPersistedOptions,
} from '@/utils/product/product-context-helpers';

const DEFAULT_SELECTED_OPTIONS = { size: 'Queen' };
const URL_BASE = 'https://example.com';

// URL() won't parse bare CMS paths like "/products/foo". Fake the origin and move on.
function parseRelativeUrl(url) {
  if (!url) {
    return null;
  }

  try {
    return new URL(url, URL_BASE);
  } catch {
    return null;
  }
}

function isPlainObject(value) {
  return Object.prototype.toString.call(value) === '[object Object]';
}

function getPersistedStoreValue(key) {
  const store = window.localStorage.getItem(STORE_NAME);

  if (!store) {
    return null;
  }

  try {
    const parsedStore = JSON.parse(store);

    return parsedStore?.[key] ?? null;
  } catch {
    window.localStorage.removeItem(STORE_NAME);

    return null;
  }
}

export const getLastSelectedOptions = () => {
  if (typeof window === 'undefined') {
    return DEFAULT_SELECTED_OPTIONS;
  }

  try {
    const persistedOptions = getPersistedStoreValue(
      PERSISTED_VARIANT_OPTIONS_KEY,
    );

    if (isPlainObject(persistedOptions)) {
      return persistedOptions;
    }

    return DEFAULT_SELECTED_OPTIONS;
  } catch (error) {
    console.error('Error reading persisted variant options:', error);

    return DEFAULT_SELECTED_OPTIONS;
  }
};

export function getVariantFromUrl(
  shopifyProduct,
  url,
  fallbackSelectedOptions,
) {
  if (!shopifyProduct?.variants?.length || !url) {
    return null;
  }

  const parsedUrl = parseRelativeUrl(url);

  if (!parsedUrl) {
    return null;
  }

  const variantId = parsedUrl.searchParams.get('variant');

  if (variantId) {
    const variantMatch = shopifyProduct.variants.find(
      (variant) =>
        variant.id === variantId || variant.id.endsWith(`/${variantId}`),
    );

    if (variantMatch) {
      return variantMatch;
    }
  }

  const optionValues = Object.fromEntries(
    (shopifyProduct.options || []).map(({ name }) => [
      name.toLowerCase(),
      parsedUrl.searchParams.get(name.toLowerCase()),
    ]),
  );
  const { appliedOptions, selectedVariant } = applyOptionValuesToVariant(
    shopifyProduct,
    fallbackSelectedOptions,
    optionValues,
  );

  if (!appliedOptions.length) {
    return null;
  }

  return selectedVariant;
}

export function getProductHandleFromUrl(url) {
  const parsedUrl = parseRelativeUrl(url);

  return parsedUrl?.pathname.match(/^\/products\/([^/?#]+)/)?.[1] ?? null;
}

export function getCollectionHandleFromUrl(url) {
  const parsedUrl = parseRelativeUrl(url);

  return parsedUrl?.pathname.match(/^\/collections\/([^/?#]+)/)?.[1] ?? null;
}

export function getUrlWithSelectedVariant(url, selectedVariant) {
  if (!url || !selectedVariant?.selectedOptions?.length) {
    return url;
  }

  const parsedUrl = parseRelativeUrl(url);

  if (!parsedUrl) {
    return url;
  }

  selectedVariant.selectedOptions.forEach(({ name, value }) => {
    parsedUrl.searchParams.set(name.toLowerCase(), value);
  });

  return url.startsWith('http')
    ? parsedUrl.toString()
    : `${parsedUrl.pathname}${parsedUrl.search}${parsedUrl.hash}`;
}

export function getVariantBasedOnLastSelectedOptions(
  shopifyProduct,
  initialSelectedOptions,
) {
  return applyPersistedOptions(
    shopifyProduct,
    initialSelectedOptions,
    getLastSelectedOptions(),
  );
}
